import { HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FeatherModule } from 'angular-feather';
import { Activity, Airplay, AlertCircle, AlertOctagon, AlertTriangle, AlignCenter, AlignJustify, AlignLeft, AlignRight, Anchor, Aperture, Archive, ArrowDown, ArrowDownCircle, ArrowDownLeft, ArrowDownRight, ArrowLeft, ArrowLeftCircle, ArrowRight, ArrowRightCircle, ArrowUp, ArrowUpCircle, ArrowUpLeft, ArrowUpRight, AtSign, Award, BarChart, BarChart2, Battery, BatteryCharging, Bell, BellOff, Bluetooth, Bold, Book, Bookmark, BookOpen, Box, Briefcase, Calendar, Camera, CameraOff, Cast, Check, CheckCircle, CheckSquare, ChevronDown, ChevronLeft, ChevronRight, ChevronsDown, ChevronsLeft, ChevronsRight, ChevronsUp, ChevronUp, Chrome, Circle, Clipboard, Clock, Cloud, CloudDrizzle, CloudLightning, CloudOff, CloudRain, CloudSnow, Code, Codepen, Codesandbox, Coffee, Columns, Command, Compass, Copy, CornerDownLeft, CornerDownRight, CornerLeftDown, CornerLeftUp, CornerRightDown, CornerRightUp, CornerUpLeft, CornerUpRight, Cpu, CreditCard, Crop, Crosshair, Database, Delete, Disc, DollarSign, Download, DownloadCloud, Droplet, Edit, Edit2, Edit3, ExternalLink, Eye, EyeOff, Facebook, FastForward, Feather, Figma, File, FileMinus, FilePlus, FileText, Film, Filter, Flag, Folder, FolderMinus, FolderPlus, Frown, Gift, GitBranch, GitCommit, Github, Gitlab, GitMerge, GitPullRequest, Globe, Grid, HardDrive, Hash, Headphones, Heart, HelpCircle, Hexagon, Home, Image, Inbox, Info, Instagram, Italic, Key, Layers, Layout, LifeBuoy, Link, Link2, Linkedin, List, Loader, Lock, LogIn, LogOut, Mail, Map, MapPin, Maximize, Maximize2, Meh, Menu, MessageCircle, MessageSquare, Mic, MicOff, Minimize, Minimize2, Minus, MinusCircle, MinusSquare, Monitor, Moon, MoreHorizontal, MoreVertical, MousePointer, Move, Music, Navigation, Navigation2, Octagon, Package, Paperclip, Pause, PauseCircle, PenTool, Percent, Phone, PhoneCall, PhoneForwarded, PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing, PieChart, Play, PlayCircle, Plus, PlusCircle, PlusSquare, Pocket, Power, Printer, Radio, RefreshCcw, RefreshCw, Repeat, Rewind, RotateCcw, RotateCw, Rss, Save, Scissors, Search, Send, Server, Settings, Share, Share2, Shield, ShieldOff, ShoppingBag, ShoppingCart, Shuffle, Sidebar, SkipBack, SkipForward, Slack, Slash, Sliders, Smartphone, Smile, Speaker, Square, Star, StopCircle, Sun, Sunrise, Sunset, Tablet, Tag, Target, Terminal, Thermometer, ThumbsDown, ThumbsUp, ToggleLeft, ToggleRight, Trash, Trash2, Trello, TrendingDown, TrendingUp, Triangle, Truck, Tv, Twitter, Type, Umbrella, Underline, Unlock, Upload, UploadCloud, User, UserCheck, UserMinus, UserPlus, Users, UserX, Video, VideoOff, Voicemail, Volume, Volume1, Volume2, VolumeX, Watch, Wifi, WifiOff, Wind, X, XCircle, XOctagon, XSquare, Youtube, Zap, ZapOff, ZoomIn, ZoomOut } from 'angular-feather/icons';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { MomentModule } from 'ngx-moment';
import { SearchDeliveryComponent } from '@routes/components/search-delivery/search-delivery.component';
import { BoxesGridComponent } from '@shared/components/boxes-grid.component';
import { DeliveryDetailsComponent } from '@shared/components/delivery-details.component';
import { EditorWindowsComponent } from '@shared/components/editor-window.component';
import { FoldableContainerComponent } from '@shared/components/foldable-container.component';
import { GMapComponent } from '@shared/components/gmap.component';
import { LayoutDefaultComponent } from '@shared/components/layout-default.component';
import { LayoutWithSidebarComponent } from '@shared/components/layout-with-sidebar.component';
import { MainComponent } from '@shared/components/main.component';
import { AppNavbarComponent } from '@shared/components/navbar.component';
import { NavigationComponent } from '@shared/components/navigation.component';
import { PrintableLandscapeComponent } from '@shared/components/printable-landscape.component';
import { PrintableComponent } from '@shared/components/printable.component';
import { ShiftPickerComponent } from '@shared/components/shift-picker.component';
import { SidebarWithHeaderComponent } from '@shared/components/sidebar-with-header.component';
import { CheckPrivilegesDirective } from '@shared/directives/check-privileges.directives';
import { DropdownMenuDirective } from '@shared/directives/dropdown-menu.directive';
import { DropdownToggleDirective } from '@shared/directives/dropdown-toggle.directive';
import { DropdownDirective } from '@shared/directives/dropdown.directive';
import { DateRangePickerComponent } from './analytics/components/date-range-picker.component';
import { ConvertToLocalTimePipe } from '@shared/pipes/convert-to-local-time.pip';
import { CheckClientDirective } from '@shared/directives/check-client.directive';
import { UtcToLocalTimePipe } from '@shared/pipes/utc-to-local-time.pipe';
import { ShiftManagementComponent } from './calendar/components/shift-management/shift-management.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { SortByPipe } from '@shared/pipes/sort-by.pipe';
import { SecToHoursPipe } from './visualiser/pipes/sec-to-hours.pipe';
import { LoaderComponent } from './shared/components/loader.component';
import { CheckFeaturesConfigurationDirective } from '@shared/directives/check-features-config.directive';
import { AccessDeniedComponent } from './shared/components/access-denied.component';
import { CurrencyMaskModule, CURRENCY_MASK_CONFIG, CurrencyMaskConfig } from "ng2-currency-mask";
import { ProxyComponent } from '@shared/components/proxy.component';
import { FindActivityByTypePipe } from '@shared/pipes/find-activity-by-type.pipe';
import { ApiErrorTranslationsComponent } from '@shared/components/api-error-translations.component';
import { FindWarehousePipe } from '@shared/pipes/find-warehouse.pipe';
import { MenuItemHasChildrenDirective } from '@shared/directives/menu-item-has-children.directive';
import { CheckPermissionsPipe } from '@shared/pipes/check-permissions.pipe';
import { AddressLabelPipe } from 'projects/inpost/src/app/operations/pipes/address-label.pipe';
import { RouterOutletComponent } from './shared/components/router-outlet.component';
import { GetObjectValuesPipe } from 'projects/inpost/src/app/operations/pipes/get-object-values.pipe';
import { TimeFormatterPipe } from '@shared/pipes/time-formatter.pipe';
import { LocalStorageService } from '@services/local-storage.service';
import { CrudEditComponent } from '@shared/components/cruds/crud-edit.component';
import { CrudListComponent } from '@shared/components/cruds/crud-list.component';
import { IsRequiredControlPipe } from '@shared/pipes/is-required-control.pipe';
import { FindByIdPipe } from '@shared/pipes/find-by-id.pipe';
import { TooltipLabelPipe } from '@shared/pipes/tooltip-label.pipe';
import { AuditLogService } from '@services/audit-log.service';
import { AuditLogComponent } from '@shared/components/audit-log/audit-log.component';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { TruncatePipe } from '@shared/pipes/truncate.pipe';
import { SumByPipe } from './visualiser/pipes/sumby.pipe';
import { FormatBagNumberPipe } from '@shared/pipes/format-bag-ref.pipe';
import {DashboardMapVehicleMarkerPipe} from '@shared/pipes/dashboard-map-vehicle-marker.pipe';
import { OperationsMainComponent } from 'projects/inpost/src/app/operations/components/operations.component';
import { TimeWindowPipe } from 'projects/inpost/src/app/operations/pipes/time-window.pipe';
import { DashboardChartComponent } from './dashboard/components/dashboard-chart/dashboard-chart.component';
import { SearchCustomerComponent } from '@shared/components/search-customer/search-customer.component';
import { AuditLogContentComponent } from '@shared/components/audit-log-content/audit-log-content.component';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { plLocale, enGbLocale } from 'ngx-bootstrap/locale';
import { AngularNotifierOptions } from '@shared/constants/angular-notifier-options.constants';
import { GoogleMapsModule } from '@angular/google-maps';
import { GoogleMapsLoaderService } from './shared/services/maps/googleMapsLoader.service';
import { AngularMultiSelectModule } from 'angular2-multiselect-dropdown';
import { GmapClusterComponent } from '@shared/components/gmap-cluster.component';
import { ToastrModule } from 'ngx-toastr';
import { PolishTimezonePipe } from '@shared/pipes/polish-timezone.pipe';

const icons = { Activity, Airplay, AlertCircle, AlertOctagon, AlertTriangle, AlignCenter, AlignJustify, AlignLeft, AlignRight, Anchor, Aperture, Archive, ArrowDownCircle, ArrowDownLeft, ArrowDownRight, ArrowDown, ArrowLeftCircle, ArrowLeft, ArrowRightCircle, ArrowRight, ArrowUpCircle, ArrowUpLeft, ArrowUpRight, ArrowUp, AtSign, Award, BarChart2, BarChart, BatteryCharging, Battery, BellOff, Bell, Bluetooth, Bold, BookOpen, Book, Bookmark, Box, Briefcase, Calendar, CameraOff, Camera, Cast, CheckCircle, CheckSquare, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsDown, ChevronsLeft, ChevronsRight, ChevronsUp, Chrome, Circle, Clipboard, Clock, CloudDrizzle, CloudLightning, CloudOff, CloudRain, CloudSnow, Cloud, Code, Codepen, Codesandbox, Coffee, Columns, Command, Compass, Copy, CornerDownLeft, CornerDownRight, CornerLeftDown, CornerLeftUp, CornerRightDown, CornerRightUp, CornerUpLeft, CornerUpRight, Cpu, CreditCard, Crop, Crosshair, Database, Delete, Disc, DollarSign, DownloadCloud, Download, Droplet, Edit2, Edit3, Edit, ExternalLink, EyeOff, Eye, Facebook, FastForward, Feather, Figma, FileMinus, FilePlus, FileText, File, Film, Filter, Flag, FolderMinus, FolderPlus, Folder, Frown, Gift, GitBranch, GitCommit, GitMerge, GitPullRequest, Github, Gitlab, Globe, Grid, HardDrive, Hash, Headphones, Heart, HelpCircle, Hexagon, Home, Image, Inbox, Info, Instagram, Italic, Key, Layers, Layout, LifeBuoy, Link2, Link, Linkedin, List, Loader, Lock, LogIn, LogOut, Mail, MapPin, Map, Maximize2, Maximize, Meh, Menu, MessageCircle, MessageSquare, MicOff, Mic, Minimize2, Minimize, MinusCircle, MinusSquare, Minus, Monitor, Moon, MoreHorizontal, MoreVertical, MousePointer, Move, Music, Navigation2, Navigation, Octagon, Package, Paperclip, PauseCircle, Pause, PenTool, Percent, PhoneCall, PhoneForwarded, PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing, Phone, PieChart, PlayCircle, Play, PlusCircle, PlusSquare, Plus, Pocket, Power, Printer, Radio, RefreshCcw, RefreshCw, Repeat, Rewind, RotateCcw, RotateCw, Rss, Save, Scissors, Search, Send, Server, Settings, Share2, Share, ShieldOff, Shield, ShoppingBag, ShoppingCart, Shuffle, Sidebar, SkipBack, SkipForward, Slack, Slash, Sliders, Smartphone, Smile, Speaker, Square, Star, StopCircle, Sun, Sunrise, Sunset, Tablet, Tag, Target, Terminal, Thermometer, ThumbsDown, ThumbsUp, ToggleLeft, ToggleRight, Trash2, Trash, Trello, TrendingDown, TrendingUp, Triangle, Truck, Tv, Twitter, Type, Umbrella, Underline, Unlock, UploadCloud, Upload, UserCheck, UserMinus, UserPlus, UserX, User, Users, VideoOff, Video, Voicemail, Volume1, Volume2, VolumeX, Volume, Watch, WifiOff, Wifi, Wind, XCircle, XOctagon, XSquare, X, Youtube, ZapOff, Zap, ZoomIn, ZoomOut };
defineLocale('pl', plLocale);
defineLocale('en', enGbLocale);

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, '/assets/i18n/');
}

export const CustomCurrencyMaskConfig: CurrencyMaskConfig = {
    align: "left",
    allowNegative: true,
    decimal: ",",
    precision: 2,
    prefix: "",
    suffix: " zł",
    thousands: "."
  };

@NgModule({
    imports: [
        HttpClientModule,
        CommonModule,
        TranslateModule.forChild({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            },

            isolate: false
        }),
        FeatherModule.pick(icons),
        TooltipModule.forRoot(),
        GoogleMapsModule,
        AngularMultiSelectModule,
        MomentModule,
        RouterModule,
        TypeaheadModule.forRoot(),
        ReactiveFormsModule,
        FormsModule,
        CurrencyMaskModule,
        NgxDatatableModule,
        BsDatepickerModule.forRoot(),
        DragDropModule,
        ToastrModule.forRoot(AngularNotifierOptions),
    ],
    declarations: [
        ShiftPickerComponent,
        LayoutDefaultComponent,
        LayoutWithSidebarComponent,
        SidebarWithHeaderComponent,
        DeliveryDetailsComponent,
        BoxesGridComponent,
        NavigationComponent,
        AppNavbarComponent,
        MainComponent,
        PrintableComponent,
        PrintableLandscapeComponent,
        DropdownMenuDirective,
        DropdownToggleDirective,
        DropdownDirective,
        EditorWindowsComponent,
        FoldableContainerComponent,
        GMapComponent,
        DateRangePickerComponent,
        SearchDeliveryComponent,
        SecToHoursPipe,
        CheckPrivilegesDirective,
        ConvertToLocalTimePipe,
        CheckClientDirective,
        CheckFeaturesConfigurationDirective,
        UtcToLocalTimePipe,
        ShiftManagementComponent,
        SortByPipe,
        LoaderComponent,
        AccessDeniedComponent,
        ProxyComponent,
        FindActivityByTypePipe,
        ApiErrorTranslationsComponent,
        FindWarehousePipe,
        MenuItemHasChildrenDirective,
        CheckPermissionsPipe,
        AddressLabelPipe,
        RouterOutletComponent,
        GetObjectValuesPipe,
        TimeFormatterPipe,
        CrudListComponent,
        CrudEditComponent,
        IsRequiredControlPipe,
        FindByIdPipe,
        TooltipLabelPipe,
        AuditLogComponent,
        AuditLogContentComponent,
        TruncatePipe,
        SumByPipe,
        FormatBagNumberPipe,
        DashboardMapVehicleMarkerPipe,
        OperationsMainComponent,
        TimeWindowPipe,
        DashboardChartComponent,
        SearchCustomerComponent,
        GmapClusterComponent,
        PolishTimezonePipe,
    ],
    providers: [
        { provide: CURRENCY_MASK_CONFIG, useValue: CustomCurrencyMaskConfig },
        LocalStorageService,
        AuditLogService,
        GoogleMapsLoaderService,
    ],
    exports: [
        ShiftPickerComponent,
        LayoutDefaultComponent,
        LayoutWithSidebarComponent,
        SidebarWithHeaderComponent,
        FoldableContainerComponent,
        HttpClientModule,
        TranslateModule,
        ToastrModule,
        DeliveryDetailsComponent,
        BoxesGridComponent,
        NavigationComponent,
        AppNavbarComponent,
        RouterModule,
        MomentModule,
        EditorWindowsComponent,
        PrintableComponent,
        PrintableLandscapeComponent,
        DropdownMenuDirective,
        DropdownToggleDirective,
        DropdownDirective,
        SearchDeliveryComponent,
        GMapComponent,
        DateRangePickerComponent,
        TypeaheadModule,
        CheckPrivilegesDirective,
        FeatherModule,
        ConvertToLocalTimePipe,
        CheckClientDirective,
        UtcToLocalTimePipe,
        ShiftManagementComponent,
        SortByPipe,
        SecToHoursPipe,
        CheckFeaturesConfigurationDirective,
        LoaderComponent,
        CurrencyMaskModule,
        MainComponent,
        FindActivityByTypePipe,
        AngularMultiSelectModule,
        FindWarehousePipe,
        MenuItemHasChildrenDirective,
        CheckPermissionsPipe,
        AddressLabelPipe,
        GetObjectValuesPipe,
        TimeFormatterPipe,
        CrudListComponent,
        CrudEditComponent,
        IsRequiredControlPipe,
        FindByIdPipe,
        TooltipLabelPipe,
        SumByPipe,
        FormatBagNumberPipe,
        DashboardMapVehicleMarkerPipe,
        OperationsMainComponent,
        TimeWindowPipe,
        DashboardChartComponent,
        SearchCustomerComponent,
        ReactiveFormsModule,
        FormsModule,
        BsDatepickerModule,
        DragDropModule,
        GmapClusterComponent,
        GoogleMapsModule,
        PolishTimezonePipe,
    ]
})
export class SharedModule {}
